import React from "react";
import Form from "../form/form";

// Existing BizVeda enquiry endpoint (shared multi-site gateway).
// Override at build time with REACT_APP_ENQUIRY_ENDPOINT if it ever changes.
const ENQUIRY_ENDPOINT =
  process.env.REACT_APP_ENQUIRY_ENDPOINT ||
  "https://thetravelos.avanicimcon.com/api/inquiries";

const INTEREST_OPTIONS = [
  { value: "", name: "Select an area of interest" },
  { value: "Managed Services", name: "Managed Services" },
  { value: "Dedicated Engineering Teams", name: "Dedicated Engineering Teams" },
  { value: "Legacy Modernization", name: "Legacy Modernization" },
  { value: "Enterprise AI Enablement", name: "Enterprise AI Enablement" },
  { value: "IT Consulting Services", name: "IT Consulting Services" },
  { value: "DXchange", name: "DXchange" },
  { value: "Travel & Hospitality", name: "Travel & Hospitality" },
  { value: "Healthcare", name: "Healthcare" },
  { value: "Insurance", name: "Insurance" },
  { value: "Legal", name: "Legal" },
];

class ContactForm extends Form {
  state = {
    data: {
      name: "",
      company: "",
      email: "",
      phone: "",
      interest: "",
      message: "",
    },
    errors: {},
    sent: false,
    submitting: false,
    error: "",
  };

  // Map the form into the existing BizVeda inquiry structure. Fields with no
  // direct column (company, interest, phone) are folded into the message so
  // nothing is lost. Phone is optional here but required by BizVeda, so a
  // "Not provided" fallback is sent when blank.
  buildEnquiryPayload = () => {
    const { data } = this.state;
    const message = [
      "Customer Message",
      "",
      data.message,
      "",
      "----------------------------------------",
      "",
      "Additional Information",
      "",
      `Company: ${data.company || "-"}`,
      `Area of Interest: ${data.interest || "-"}`,
      `Phone: ${data.phone || "Not provided"}`,
    ].join("\n");

    return {
      name: data.name,
      email: data.email,
      phone: data.phone || "Not provided",
      title: `Website enquiry from ${data.name}`,
      message,
      source: "Avani Cimcon Website",
    };
  };

  handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const errors = this.validate();
    this.setState({ errors: errors || {} });
    if (errors) return;

    // Prevent duplicate submissions while a request is in progress.
    if (this.state.submitting) return;
    this.setState({ submitting: true, error: "" });

    try {
      const response = await fetch(ENQUIRY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(this.buildEnquiryPayload()),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const result = await response.json().catch(() => ({}));
      if (!result.ok) {
        throw new Error("Enquiry was not accepted by the server");
      }

      // Mark success only after a confirmed response.
      this.setState({ sent: true, submitting: false });
    } catch (err) {
      this.setState({
        submitting: false,
        error:
          "Sorry, we couldn't submit your enquiry right now. Please try again, or email us at inquiry@avanicimcon.com.",
      });
    }
  };

  validate = () => {
    const errors = {};
    const { data } = this.state;

    if (!this.validateFormData(data.name, "require")) errors.name = "Name required";
    else if (!this.validateFormData(data.name, "length", { min: 2, max: 60 }))
      errors.name = "Name must be between 2 and 60 characters";

    if (!this.validateFormData(data.company, "require")) errors.company = "Company required";
    else if (!this.validateFormData(data.company, "length", { min: 2, max: 80 }))
      errors.company = "Company must be between 2 and 80 characters";

    if (!this.validateFormData(data.email, "require")) errors.email = "Email required";
    else if (!this.validateFormData(data.email, "email")) errors.email = "Enter a valid email";

    if (data.phone && !/^[0-9+().\-\s]{7,20}$/.test(data.phone)) {
      errors.phone = "Enter a valid phone number";
    }

    if (!this.validateFormData(data.interest, "require")) {
      errors.interest = "Choose an area of interest";
    }

    if (!this.validateFormData(data.message, "require")) errors.message = "Message required";
    else if (!this.validateFormData(data.message, "length", { min: 20, max: 2000 }))
      errors.message = "Message should be between 20 and 2000 characters";

    return Object.keys(errors).length === 0 ? null : errors;
  };

  render() {
    return (
      <div className="shadow-sm p-4">
        <p>
          Tell us what you are building, modernizing or trying to stabilize. We will route your
          message to the right enterprise conversation.
        </p>

        <form onSubmit={this.handleSubmit}>
          <div className="row">
            <div className="col-lg-4">{this.renderInput("name", "Name *")}</div>
            <div className="col-lg-4">{this.renderInput("company", "Company *")}</div>
            <div className="col-lg-4">{this.renderInput("email", "Email *", "email")}</div>
          </div>

          <div className="row">
            <div className="col-lg-4">{this.renderInput("phone", "Phone (optional)")}</div>
            <div className="col-lg-4">
              {this.renderSelect("interest", "Area of Interest *", INTEREST_OPTIONS)}
            </div>
            <div className="col-lg-4 d-flex align-items-end">
              <button
                type="submit"
                className="btn btn-primary"
                style={{ minWidth: "180px", marginBottom: "1rem" }}
                disabled={this.state.submitting || this.state.sent}
              >
                {this.state.submitting ? "Sending..." : "Start a Conversation"}
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              {this.renderTextarea("message", "Message *")}
            </div>
          </div>
        </form>

        {this.state.sent && (
          <div className="alert alert-success mt-3">
            <p className="font-weight-bold mt-3">
              Thank you — your enquiry has been submitted successfully.
            </p>
            <p className="mb-0">
              Our team will get back to you shortly. You can also reach us at{" "}
              <a className="text-primary" href="mailto:inquiry@avanicimcon.com">
                inquiry@avanicimcon.com
              </a>
              .
            </p>
          </div>
        )}

        {this.state.error && (
          <div className="alert alert-danger mt-3">
            <p className="mb-0">{this.state.error}</p>
          </div>
        )}
      </div>
    );
  }
}

export default ContactForm;
